import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Chip } from '../../../../components/common/chip/Chip';
import { Button } from '../../../../components/common/buttons/Button';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { RadioCard } from '../../../../components/common/Radio/RadioCard';
import { useNotification } from '../../../../provider/ToastNotificationProvider';
import { useNavigate } from 'react-router-dom';
import { managedFarmPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { IManagedFarmTask, managedFarmTaskStatus } from '../../../../interfaces/ManagedFarm.interfaces';
import { format, getMonth } from 'date-fns';
import { ampli } from '../../../../ampli';
import { updateTaskStatusById } from '../../../../utils/manager/managedFarmTaskManager.util';

interface ITaskCardProps {
  task: IManagedFarmTask;
  onStatusUpdate: () => void;
}

export const TaskCard = ({ task, onStatusUpdate }: ITaskCardProps) => {
  const navigate = useNavigate();
  const { openNotification } = useNotification();
  // TODO : 로컬 스토리지 상태 연동 PVM-3526
  const [selectedStatus, setSelectedStatus] = useState<managedFarmTaskStatus>(task.status);
  const [isStatusChangeModalOpen, setIsStatusChangeModalOpen] = useState<boolean>(false);

  const taskTitle = useMemo<string>(() => {
    if (!task) {
      return '';
    }

    return `${task.landInfo?.addressTrailingParts} ${task.cropType !== '기타' ? task.cropType : task.cropTypeEtc}`;
  }, [task]);

  const taskSchedule = useMemo<string | null>(() => {
    if (!task || !task.startDate || !task.endDate) {
      return null;
    }

    return `${format(task.startDate, 'yy.MM.dd')}~${format(task.endDate, 'yy.MM.dd')}`;
  }, [task]);

  const goToDetailPage = () => {
    navigate(`${managedFarmPageInfo.taskDetailPage.path}/${task.id}`);
  };

  const handleMoreDetailButtonClick = () => {
    if (taskTitle && task.status && task.startDate) {
      ampli.pvmClickGoToManagedFarmTaskDetailPageButton({
        task_title: taskTitle,
        task_status: task.status,
        task_start_year: format(task.startDate, 'yy'),
        task_start_month: String(getMonth(task.startDate) + 1),
        task_start_day: format(task.startDate, 'dd'),
      });
    }

    goToDetailPage();
  };

  const handleStatusChangeButtonClick = () => {
    if (taskTitle && task.status && task.startDate) {
      ampli.pvmClickManagedFarmTaskStatusChangeButton({
        task_title: taskTitle,
        task_status: task.status,
        task_start_year: format(task.startDate, 'yy'),
        task_start_month: String(getMonth(task.startDate) + 1),
        task_start_day: format(task.startDate, 'dd'),
      });
    }

    setIsStatusChangeModalOpen(true);
  };

  const changeTaskStatus = async (newStatus: managedFarmTaskStatus) => {
    try {
      await updateTaskStatusById(task.id, newStatus);
      ampli.pvmChangeManagedFarmTaskStatus({
        changed_status: newStatus,
      });
      onStatusUpdate();
    } catch (error) {
      console.error('태스크 상태 변경 실패:', error);
    }
  };

  if (!task) {
    return null;
  }

  return (
    <>
      <Container>
        <TextSection>
          <TitleStatusContainer>
            <Title>{taskTitle}</Title>
            <Chip
              type={'square'}
              color={task.status === '작업 예정' ? 'primary' : task.status === '작업 중' ? 'secondary' : 'grey'}
              theme={'light'}
            >
              {task.status}
            </Chip>
          </TitleStatusContainer>
          <TaskDate>{taskSchedule}</TaskDate>
        </TextSection>
        <ButtonContainer>
          <ButtonWrapper>
            <Button
              borderType={'square'}
              color={'filledGreyLight'}
              size={'md'}
              text={{ size: 'md', content: '작업 정보 보기' }}
              onClick={handleMoreDetailButtonClick}
            />
          </ButtonWrapper>
          <ButtonWrapper>
            <Button
              borderType={'square'}
              color={'filledGreyLight'}
              size={'md'}
              text={{ size: 'md', content: '작업 상태 변경' }}
              onClick={handleStatusChangeButtonClick}
            />
          </ButtonWrapper>
        </ButtonContainer>
      </Container>

      <PopupConfirmModal
        isShow={isStatusChangeModalOpen}
        hasCloseButton={false}
        title={'변경할 작업 상태를\n선택해주세요.'}
        element={
          <StatusButtonContainer>
            <RadioCard
              key={'작업 예정'}
              title={'작업 예정'}
              onClick={() => setSelectedStatus('작업 예정')}
              isActive={selectedStatus === '작업 예정'}
            />
            <RadioCard
              key={'작업 중'}
              title={'작업 중'}
              onClick={() => setSelectedStatus('작업 중')}
              isActive={selectedStatus === '작업 중'}
            />
            <RadioCard
              key={'작업 완료'}
              title={'작업 완료'}
              onClick={() => setSelectedStatus('작업 완료')}
              isActive={selectedStatus === '작업 완료'}
            />
          </StatusButtonContainer>
        }
        buttonArray={[
          {
            text: '변경하기',
            onClick: () => {
              changeTaskStatus(selectedStatus);
              setIsStatusChangeModalOpen(false);
              openNotification({ message: '작업 상태 변경이 완료되었습니다.' });
            },
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => setIsStatusChangeModalOpen(false),
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;

  padding: 24px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-white, #fff);
`;

const TextSection = styled.div`
  width: 100%;
  height: 49px;

  padding: 0px 6px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Body/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
`;

const TitleStatusContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TaskDate = styled.div`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const ButtonWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StatusButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;
